import { fDate } from '@/utils/dayjs'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { fNumber } from '@/utils/format-number'
import { Card, CardContent } from '@/components/ui/card'
import { MoreHorizontal, Eye, Edit, Trash2 } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

import { Article } from './types'

interface ArticleTableProps {
  articles: Article[]
  loading: boolean
  onView?: (article: Article) => void
  onEdit?: (article: Article) => void
  onDelete?: (article: Article) => void
}

const getStatusBadge = (isPublished: boolean) => {
  if (isPublished) {
    return <Badge className="bg-green-500 text-background">เผยแพร่</Badge>
  }
  return <Badge className="bg-red-500 text-background">ปิดใช้งาน</Badge>
}

export default function ArticleTable({
  articles,
  loading,
  onView,
  onEdit,
  onDelete,
}: ArticleTableProps) {
  if (loading) {
    return (
      <Card>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">รูป</TableHead>
                <TableHead>ชื่อหัวข้อ</TableHead>
                <TableHead className="w-24">สถานะ</TableHead>
                <TableHead>ผู้เขียน</TableHead>
                <TableHead className="w-24">จำนวนคนดู</TableHead>
                <TableHead className="w-32">วันที่อัปเดต</TableHead>
                <TableHead className="w-16">การจัดการ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <div className="w-12 h-12 bg-gray-200 rounded-lg animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-16 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-8 w-8 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    )
  }
  if (articles.length === 0) {
    return (
      <div className="border rounded-lg p-8 text-center">
        <p className="text-base text-mute">ไม่พบข้อมูล RFC Talk</p>
      </div>
    )
  }

  return (
    <Card className="p-0">
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">รูป</TableHead>
              <TableHead>ชื่อหัวข้อ</TableHead>
              <TableHead className="w-24">สถานะ</TableHead>
              <TableHead>ผู้เขียน</TableHead>
              <TableHead className="w-24">จำนวนคนดู</TableHead>
              <TableHead className="w-32">วันที่อัปเดต</TableHead>
              <TableHead className="w-16">การจัดการ</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {articles.map((article) => (
              <TableRow key={article.id}>
                <TableCell>
                  <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100">
                    {article.imageHeaderUrl ? (
                      <img
                        src={article.imageHeaderUrl}
                        alt={article.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                        <span className="text-xs text-gray-400">No image</span>
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    <p className="text-base font-medium text-black line-clamp-2">
                      {article.title}
                    </p>
                  </div>
                </TableCell>
                <TableCell>{getStatusBadge(article.isPublished)}</TableCell>
                <TableCell>
                  <p className="text-base text-black">
                    {article.createdByDisplayName}
                  </p>
                </TableCell>
                <TableCell>
                  <p className="text-base text-black">
                    {fNumber(article.viewCount)}
                  </p>
                </TableCell>
                <TableCell>
                  <p className="text-sm text-mute">
                    {fDate(article.updatedAt)}
                  </p>
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {onView && (
                        <DropdownMenuItem onClick={() => onView(article)}>
                          <Eye className="mr-2 h-4 w-4" />
                          ดูรายละเอียด
                        </DropdownMenuItem>
                      )}
                      {onEdit && (
                        <DropdownMenuItem onClick={() => onEdit(article)}>
                          <Edit className="mr-2 h-4 w-4" />
                          แก้ไข
                        </DropdownMenuItem>
                      )}
                      {onDelete && (
                        <DropdownMenuItem
                          onClick={() => onDelete(article)}
                          className="text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          ลบ
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}
